import React, { useState, useEffect, useRef } from 'react';
import { Zap, Trophy, Timer, Play, RotateCcw, TrendingUp, Clock, Shield } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

// Animated number counter
const AnimatedNumber = ({ end, suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime = null;
    const duration = 1500;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;
      if (progress < 1) {
        setCount(end * (1 - Math.pow(1 - progress, 3)));
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end]);

  return <span ref={ref}>{count.toFixed(decimals)}{suffix}</span>;
};

// Racing bar component with animated fill
const RacingBar = ({ name, latency, latencyDisplay, color, delay, isRacing, maxLatency, winner, logo }) => {
  const percentage = Math.min((latency / maxLatency) * 100, 100);
  const [currentWidth, setCurrentWidth] = useState(0);

  useEffect(() => {
    if (isRacing) {
      const timer = setTimeout(() => {
        setCurrentWidth(percentage);
      }, delay);
      return () => clearTimeout(timer);
    } else {
      setCurrentWidth(0);
    }
  }, [isRacing, percentage, delay]);

  const isWinner = winner === name;

  return (
    <div className="relative mb-6 last:mb-0">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color.bg} ${color.border} border`}>
            {logo}
          </div>
          <span className={`font-semibold ${isWinner ? 'text-white' : 'text-muted-foreground'}`}>
            {name}
          </span>
          {isWinner && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-xs font-bold">
              <Trophy className="w-3 h-3" /> WINNER
            </span>
          )}
        </div>
        <div className={`font-mono text-sm ${isWinner ? color.text : 'text-muted-foreground'}`}>
          {latencyDisplay || `${latency}ns`}
        </div>
      </div>

      {/* Track */}
      <div className="relative h-10 bg-card/50 rounded-full overflow-hidden border border-border">
        {/* Animated racing bar */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-[2000ms] ease-out ${color.gradient}`}
          style={{ width: `${currentWidth}%` }}
        >
          {/* Glow effect */}
          <div className={`absolute inset-0 ${color.glow} blur-md opacity-50`} />

          {/* Racing particles */}
          {isRacing && currentWidth > 0 && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex gap-1">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className={`w-1 h-1 rounded-full ${color.dot} animate-ping`}
                  style={{ animationDelay: `${i * 100}ms` }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Finish line markers */}
        <div className="absolute inset-y-0 right-0 w-px bg-white/20" />
        <div className="absolute inset-y-0 right-[25%] w-px bg-white/10" />
        <div className="absolute inset-y-0 right-[50%] w-px bg-white/10" />
        <div className="absolute inset-y-0 right-[75%] w-px bg-white/10" />
      </div>
    </div>
  );
};

// Live ticker component showing real-time "decisions"
const LiveTicker = ({ isActive }) => {
  const [decisions, setDecisions] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setDecisions(0);
      return;
    }

    const interval = setInterval(() => {
      setDecisions(prev => prev + Math.floor(Math.random() * 50000000) + 40000000);
    }, 100);

    return () => clearInterval(interval);
  }, [isActive]);

  return (
    <div className="font-mono text-sm text-green-400 flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
      </span>
      <span className="text-muted-foreground">Decisions:</span>
      <span className="tabular-nums">{decisions.toLocaleString()}</span>
    </div>
  );
};

const LatencyRace = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.3 });
  const [isRacing, setIsRacing] = useState(false);
  const [raceComplete, setRaceComplete] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);

  const competitors = [
    {
      name: 'ASTRA Express Lane',
      latency: 2,
      latencyDisplay: '~2ns',
      color: {
        bg: 'bg-cyan-500/20',
        border: 'border-cyan-500/30',
        text: 'text-cyan-400',
        gradient: 'bg-gradient-to-r from-cyan-500 to-blue-500',
        glow: 'bg-cyan-500',
        dot: 'bg-cyan-300',
      },
      logo: <Zap className="w-4 h-4 text-cyan-400" />,
      delay: 0,
    },
    {
      name: 'Lock-Free C++ Elite',
      latency: 50,
      latencyDisplay: '~50ns',
      color: {
        bg: 'bg-orange-500/20',
        border: 'border-orange-500/30',
        text: 'text-orange-400',
        gradient: 'bg-gradient-to-r from-orange-500 to-amber-500',
        glow: 'bg-orange-500',
        dot: 'bg-orange-300',
      },
      logo: <Timer className="w-4 h-4 text-orange-400" />,
      delay: 200,
    },
    {
      name: 'Java Low-Latency',
      latency: 150,
      color: {
        bg: 'bg-blue-500/20',
        border: 'border-blue-500/30',
        text: 'text-blue-400',
        gradient: 'bg-gradient-to-r from-blue-500 to-indigo-500',
        glow: 'bg-blue-500',
        dot: 'bg-blue-300',
      },
      logo: <span className="text-xs font-bold text-blue-400">J</span>,
      delay: 400,
    },
    {
      name: 'C++ Elite HFT',
      latency: 300,
      color: {
        bg: 'bg-purple-500/20',
        border: 'border-purple-500/30',
        text: 'text-purple-400',
        gradient: 'bg-gradient-to-r from-purple-500 to-pink-500',
        glow: 'bg-purple-500',
        dot: 'bg-purple-300',
      },
      logo: <span className="text-xs font-bold text-purple-400">C++</span>,
      delay: 600,
    },
    {
      name: 'Standard Software',
      latency: 1000,
      color: {
        bg: 'bg-gray-500/20',
        border: 'border-gray-500/30',
        text: 'text-gray-400',
        gradient: 'bg-gradient-to-r from-gray-500 to-gray-600',
        glow: 'bg-gray-500',
        dot: 'bg-gray-300',
      },
      logo: <span className="text-xs font-bold text-gray-400">SW</span>,
      delay: 800,
    },
  ];

  const maxLatency = 1000; // normalize to 1000ns for visual

  // Auto-start race when section becomes visible
  useEffect(() => {
    if (isVisible && !autoStarted) {
      const timer = setTimeout(() => {
        setIsRacing(true);
        setAutoStarted(true);
        setTimeout(() => setRaceComplete(true), 2500);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoStarted]);

  const handleStartRace = () => {
    setIsRacing(false);
    setRaceComplete(false);
    setTimeout(() => {
      setIsRacing(true);
      setTimeout(() => setRaceComplete(true), 2500);
    }, 100);
  };

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-morph" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-morph" style={{ animationDelay: '-4s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-8 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
            <Trophy className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Independently Verified</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            See Why We're <span className="text-shimmer">Unbeatable</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every millisecond counts in HFT. We count in <span className="text-cyan-400 font-semibold">nanoseconds</span>.
            Watch how Astra dominates every competitor in real-time.
          </p>
        </div>

        {/* Key Stats */}
        <div
          className={`grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="p-4 rounded-xl bg-cyan-500/15 border-2 border-cyan-500/30 text-center relative">
            <div className="absolute -top-2 left-1/2 -translate-x-1/2">
              <span className="px-2 py-0.5 text-[10px] font-bold bg-cyan-500 text-white rounded-full">FASTEST</span>
            </div>
            <Zap className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-cyan-400">
              &lt;<AnimatedNumber end={2} decimals={0} suffix="ns" />
            </div>
            <div className="text-xs text-cyan-300">Decision Latency</div>
          </div>
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
            <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-green-400">
              <AnimatedNumber end={954} decimals={0} suffix="M" />
            </div>
            <div className="text-xs text-muted-foreground">Decisions/sec</div>
          </div>
          <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 text-center">
            <Shield className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-purple-400">
              ~1ns*
            </div>
            <div className="text-xs text-muted-foreground">Amortized per-decision</div>
          </div>
          <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 text-center">
            <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-blue-400">
              <AnimatedNumber end={6} decimals={0} suffix="" />
            </div>
            <div className="text-xs text-muted-foreground">Native Protocols</div>
          </div>
        </div>

        {/* Race Section Header */}
        <div className={`text-center mb-6 transition-all duration-700 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h3 className="text-xl font-bold text-white mb-2">Decision Engine Race</h3>
          <p className="text-sm text-muted-foreground">Lower latency = faster decisions</p>
        </div>

        {/* Race Track */}
        <div
          className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="p-8 rounded-3xl bg-card/50 border border-border backdrop-blur-sm">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  onClick={handleStartRace}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity"
                >
                  {raceComplete ? (
                    <>
                      <RotateCcw className="w-4 h-4" /> Replay
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" /> Start Race
                    </>
                  )}
                </button>
                <LiveTicker isActive={isRacing} />
              </div>

              <div className="text-left sm:text-right">
                <div className="text-xs text-muted-foreground mb-1">Decision Logic Latency</div>
                <div className="text-sm font-medium text-white">Lower = Faster</div>
              </div>
            </div>

            {/* Racing Bars */}
            <div className="space-y-2">
              {competitors.map((competitor) => (
                <RacingBar
                  key={competitor.name}
                  {...competitor}
                  isRacing={isRacing}
                  maxLatency={maxLatency}
                  winner={raceComplete ? 'ASTRA Express Lane' : null}
                />
              ))}
            </div>

            {/* Race Results */}
            {raceComplete && (
              <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 border border-cyan-500/20 animate-fade-in">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-cyan-400 mb-1">954M</div>
                    <div className="text-xs text-muted-foreground">Decisions/sec</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-400 mb-1">~1ns*</div>
                    <div className="text-xs text-muted-foreground">Amortized</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-400 mb-1">ARM+x86</div>
                    <div className="text-xs text-muted-foreground">Cross-platform verified</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom disclaimer */}
        <p className="text-center text-xs text-muted-foreground mt-6 max-w-2xl mx-auto">
          * Amortized per-decision time from pipelined throughput benchmark. 954M decisions/sec verified on ARM64, 450M on x86.
          <span className="text-white font-medium"> Continuous polling architecture with pre-computed indicators.</span>
        </p>
      </div>
    </section>
  );
};

export default LatencyRace;
